/**
 * PatientLink.tsx
 *
 * A drop-in replacement for <Link> / navigate() that automatically
 * adds the patient to the recent-patients sidebar on click.
 *
 * Usage:
 *   <PatientLink patientId={123} patientName="Jane Doe">Jane Doe</PatientLink>
 *   const navToPatient = usePatientNavigation();
 *   navToPatient({ patientId: 123, name: 'Jane Doe' });
 */
import { Link, type LinkProps, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';

// ── Component ────────────────────────────────────────────────────────────────

export interface PatientLinkProps extends Omit<LinkProps, 'to'> {
  patientId: number;
  patientName: string;
  photoUrl?: string | null;
}

export function PatientLink({
  patientId,
  patientName,
  photoUrl,
  onClick,
  children,
  ...rest
}: PatientLinkProps) {
  const visit = usePatientHistoryStore((s) => s.visit);

  return (
    <Link
      to={`/patients/${patientId}`}
      onClick={(e) => {
        // Pass through unchanged: undefined = "unknown", null = "known none".
        visit({ patientId, name: patientName, photoUrl });
        onClick?.(e);
      }}
      {...rest}
    >
      {children}
    </Link>
  );
}

// ── Hook for programmatic navigation ─────────────────────────────────────────

export function usePatientNavigation() {
  const navigate = useNavigate();
  const visit = usePatientHistoryStore((s) => s.visit);

  return useCallback(
    (patient: { patientId: number; name: string; photoUrl?: string | null }) => {
      visit({
        patientId: patient.patientId,
        name: patient.name,
        photoUrl: patient.photoUrl,
      });
      navigate(`/patients/${patient.patientId}`);
    },
    [navigate, visit],
  );
}
